/* Copyright (c) 2023-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import {
    AfterViewInit,
    OnInit,
    Component,
    Input,
    Directive,
    ElementRef,
    OnDestroy,
    TemplateRef,
    ViewChild,
    ViewEncapsulation,
    NgZone,
    ChangeDetectorRef,
    ChangeDetectionStrategy
} from '@angular/core';
import { CdkVirtualScrollViewport, ExtendedScrollToOptions, CdkVirtualForOf } from '@angular/cdk/scrolling';
import { BidiModule } from '@angular/cdk/bidi';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { ExtendedModule } from '@angular/flex-layout/extended';
import { NgTemplateOutlet, NgClass } from '@angular/common';
import { Subscription, fromEvent, Subject, combineLatest } from 'rxjs';
import { takeUntil, debounceTime, take } from 'rxjs/operators';
import { TranslateModule } from '@ngx-translate/core';
// TODO(AVENGERS-831): switch to our own bezier implementation
import * as BezierEasing from 'bezier-easing';
import { LoggingService, Logger } from 'crimson/lib/logging/logging.service';
import { LocaleService } from 'crimson/lib/locale-service/locale.service';
import { evalCss } from 'utils/lib/common/common-utils';
import { ResizeObserverUtils } from 'utils/lib/resize-observer/resize-observer-utils';
import { FastOneDimensionSpatialNavigationDirective } from '../../directives/fast-spatial-navigation/fast-one-dimenstion-spatial-navigation.directive';
import { FastSpatialNavigationItemDirective } from '../../directives/fast-spatial-navigation/fast-spatial-navigation-item.directive';
import { FocusBackDirective } from '../../directives/focus-back.directive';
import { KnownSizesVirtualScroll } from '../../directives/known-sizes-virtual-scroll';
import { SpatialNavigationGroupDirective } from '../../directives/spatial-navigation-container.directive';
import { SpatialNavigationVirtualScrollDirective } from '../../directives/spatial-navigation-virtual-scroll.directive';
import { ScrollPlayer } from '../../scrolling/scroll-player';
import { NavigationEvent } from '../../services/spatial-navigation/spatial-navigation-polyfill';
import { SpatialNavigationService } from '../../services/spatial-navigation/spatial-navigation.service';
import { HigCarouselItem, HigCarouseItemProperty } from './nv-hig-carousel.types';

@Directive({
    selector: '[nvHigCarouselItem]',
    standalone: true
})
export class CarouselItemDirective {
    constructor(public value: TemplateRef<any>) {}
}

@Component({
    selector: 'nv-hig-carousel',
    templateUrl: './nv-hig-carousel.component.html',
    styleUrls: ['./nv-hig-carousel.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        BidiModule,
        CdkVirtualScrollViewport,
        SpatialNavigationVirtualScrollDirective,
        KnownSizesVirtualScroll,
        SpatialNavigationGroupDirective,
        FocusBackDirective,
        FastOneDimensionSpatialNavigationDirective,
        CdkVirtualForOf,
        FastSpatialNavigationItemDirective,
        NgTemplateOutlet,
        MatButtonModule,
        NgClass,
        ExtendedModule,
        MatIconModule,
        TranslateModule
    ]
})
export class NvHigCarouselComponent<T> implements AfterViewInit, OnDestroy {
    @ViewChild('wrapper') private wrapper: ElementRef;
    @ViewChild('container', { read: ElementRef }) private sizingElement: ElementRef;
    @ViewChild('carousel', { read: ElementRef }) private carousel: ElementRef<HTMLElement>;
    @ViewChild('carousel') private container: CdkVirtualScrollViewport;
    @ViewChild(FocusBackDirective) focusBackDirective;

    @Input()
    get items(): HigCarouselItem<T>[] {
        return this._items;
    }
    set items(itemList: HigCarouselItem<T>[]) {
        let changed = false;
        if (!this._itemsOriginal || this._itemsOriginal?.length !== itemList.length) {
            changed = true;
        } else {
            for (let i = 0; i < this._items.length; i++) {
                if (
                    this._items[i].id !== itemList[i].id ||
                    this._items[i]?.boxArtImage !== itemList[i]?.boxArtImage ||
                    this._items[i]?.tvBannerImage !== itemList[i]?.tvBannerImage
                ) {
                    changed = true;
                }
            }
        }
        if (changed) {
            this._itemsOriginal = itemList;
            this._items = itemList.slice();
            if (this.initialized) {
                this.initCarousel();
            }
        }
    }
    private _itemsOriginal: HigCarouselItem<T>[];
    private _items: HigCarouselItem<T>[];

    @Input() template: any;
    @Input() itemProperty: HigCarouseItemProperty;
    @Input() containerLeftMarginStr: string;
    @Input() containerTopMarginStr: string;
    @Input() mobileMode: boolean;
    @Input() resizeDebounceTime?: number;
    private logger: Logger;
    private get scrollLeft() {
        return this.container.measureScrollOffset();
    }
    private set scrollLeft(value: number) {
        const scrollOptions: ExtendedScrollToOptions = {
            start: value,
            behavior: 'auto'
        };

        this.container.scrollTo(scrollOptions);
    }
    private scrollAmountWithButton: number;
    private scrollLimit: number;
    private firstPositiveWidthTileIndex: number;
    private currentFocusedIndex: number;

    private readonly animationDuration = 1000; // ms
    private readonly easingFunction = BezierEasing(0.35, 0.37, 0.2, 1);
    private readonly defaultResizeDebounceTime = 200;
    private animationSubscription: Subscription;

    enableButtons: boolean;
    showLeftCarouselButton = false;
    showRightCarouselButton = false;
    carouselButtonsVisibility = false;
    carouselDirection = 'ltr';

    itemOffsets: number[] = [];
    private extraMargin = 32;
    private extraMarginHeight = 8;
    itemWidth = 270; // overridden by css vars
    itemHeight = 150; // overridden by css vars
    spatialNavigationSpacingLeft = 0;
    spatialNavigationSpacingRight = 0;
    isFirstTileFocused: boolean;

    private initialized = false;
    private destroy$: Subject<boolean>;

    constructor(
        loggingService: LoggingService,
        localeService: LocaleService,
        private ngZone: NgZone,
        private spatialNavigationService: SpatialNavigationService,
        private changeDetector: ChangeDetectorRef
    ) {
        this.logger = loggingService.getLogger('Hig/CarouselComponent');
        this.carouselDirection = localeService.isRTLLayoutEnabled ? 'rtl' : 'ltr';
        this.destroy$ = new Subject();
        this.enableButtons = !this.mobileMode;
        this.resizeDebounceTime = this.defaultResizeDebounceTime;
    }

    onTileFocused(index: number) {
        const focusedIndex = index - this.firstPositiveWidthTileIndex;
        const isFirstFocused = focusedIndex === 0;
        if (this.isFirstTileFocused !== isFirstFocused) {
            this.isFirstTileFocused = isFirstFocused;
            this.changeDetector.detectChanges();
        }
        this.currentFocusedIndex = index;
    }

    onTileFocusOut() {
        this.currentFocusedIndex = -1;
    }

    ngAfterViewInit() {
        setTimeout(() => {
            this.initCarousel();
        });

        ResizeObserverUtils.onResize(this.wrapper.nativeElement)
            .pipe(debounceTime(this.resizeDebounceTime), takeUntil(this.destroy$))
            .subscribe(_ => this.resizeCarousel());

        this.ngZone.runOutsideAngular(() => {
            this.carousel.nativeElement.addEventListener('navnotarget', this.navNoTarget);

            fromEvent(this.carousel.nativeElement, 'scroll')
                .pipe(debounceTime(100), takeUntil(this.destroy$))
                .subscribe(e => {
                    this.ngZone.run(() => {
                        this.onScroll(e);
                    });
                });
        });
    }

    private get hasFocusedItem(): boolean {
        return this.currentFocusedIndex >= 0;
    }

    private navNoTarget = (event: NavigationEvent) => {
        const direction = event.detail.dir;
        // Prevents spatial navigation from navigating out the carousel horizontally
        if (direction === 'left' || direction === 'right') {
            event.preventDefault();
        }
    };

    private parseMargin(top: string, right: string = top, bottom: string = top, left: string = right) {
        return { top, right, bottom, left };
    }

    private initCarousel() {
        const cssStyles = getComputedStyle(this.sizingElement.nativeElement);
        /**
         * Width and Height CSS Variables are passed from the consumers.
         * This gives the capability to give any variables according to different tile types ( FIlter, Tall, Width) for GFN
         * and RWD widths for NVAPP.
         */
        let tileWidth = evalCss(cssStyles.getPropertyValue(this.itemProperty.widthCSSVar));
        let tileHeight = evalCss(cssStyles.getPropertyValue(this.itemProperty.heightCSSVar));
        // TODO MVPC-12840 Look for a permanent fix as negative width is not expected
        if (tileWidth < 0 || tileHeight < 0) {
            this.logger.warn('Cannot show tile as width or height is less than zero', tileWidth, tileHeight);
            return;
        }
        const tileMaxWidth = evalCss(cssStyles.getPropertyValue(this.itemProperty.maxWidthCSSVar));
        const tileMaxHeight = evalCss(cssStyles.getPropertyValue(this.itemProperty.maxHeightCSSVar));
        // Compare maxwidth and maxheight
        tileWidth = Math.min(tileWidth, +tileMaxWidth);
        tileHeight = Math.min(tileHeight, +tileMaxHeight);
        const margins = cssStyles
            .getPropertyValue(this.itemProperty.marginCSSVar)
            .trim()
            .replace(/\s+/g, ' ')
            .split(' ');
        const [top, ...rbl] = margins;
        const realMargins = this.parseMargin(top, ...rbl);

        this.extraMargin = parseInt(cssStyles.getPropertyValue(this.containerLeftMarginStr), 10);
        this.extraMarginHeight = parseInt(cssStyles.getPropertyValue(this.containerTopMarginStr), 10);
        const realMarginLeft = parseInt(realMargins.left, 10);
        const realMarginRight = parseInt(realMargins.right, 10);

        this.spatialNavigationSpacingLeft = this.extraMargin + realMarginLeft;
        this.spatialNavigationSpacingRight = this.extraMargin + realMarginRight;

        tileHeight += parseInt(realMargins.top, 10) + parseInt(realMargins.bottom, 10) + this.extraMarginHeight;
        tileWidth += realMarginLeft + realMarginRight;

        const carouselWrapperWidth = this.carousel.nativeElement.getBoundingClientRect().width;

        this.itemWidth = tileWidth;
        this.itemHeight = tileHeight;

        this.itemOffsets = [0];
        let widthAccum = 0;
        this.firstPositiveWidthTileIndex = -1;
        this.items.forEach((item, index) => {
            let width = item.visibility ? this.itemWidth : 0;
            if (item.visibility && this.firstPositiveWidthTileIndex === -1) {
                this.firstPositiveWidthTileIndex = index;
            }
            if (index === 0 || index === this.items.length - 1) {
                width += this.extraMargin;
            }
            widthAccum += width;
            this.itemOffsets.push(widthAccum);
        });
        // copy to force change detection
        this.itemOffsets = this.itemOffsets.slice();

        this.scrollLimit = this.itemOffsets[this.itemOffsets.length - 1] - carouselWrapperWidth;
        if (this.scrollLimit < this.extraMargin) {
            this.scrollLimit = 0;
        }

        const tilesToMove = this.itemWidth > 0 ? Math.floor(carouselWrapperWidth / this.itemWidth) : 0;
        this.scrollAmountWithButton = tilesToMove * this.itemWidth;
        if (this.enableButtons) {
            this.updateButtonVisibility();
        }
        this.initialized = true;
        this.changeDetector.detectChanges();
    }

    // we use jump effect instead of scroll to show the tile and hide the spinner asap
    jumpToTile(index) {
        this.scrollLeft = Math.min(this.scrollLimit, this.itemWidth * (index - this.firstPositiveWidthTileIndex));
    }

    private moveRightValue(dist: number) {
        const proposedScrollLeft = this.container.getElementRef().nativeElement.scrollLeft + dist;
        this.animateScrollLeft(proposedScrollLeft);
    }

    private moveLeftValue(dist: number) {
        const proposedScrollLeft = this.container.getElementRef().nativeElement.scrollLeft - dist;
        this.animateScrollLeft(proposedScrollLeft);
    }

    moveRight(event) {
        this.moveRightValue(this.scrollAmountWithButton);
    }

    moveLeft(event) {
        this.moveLeftValue(this.scrollAmountWithButton);
    }

    private cancelScrollAnimationIfNeeded() {
        this.animationSubscription?.unsubscribe();
        this.animationSubscription = null;
    }

    private animateScrollLeft(end: number) {
        this.cancelScrollAnimationIfNeeded();
        const scrollPlayer = new ScrollPlayer(this.container.getElementRef().nativeElement, end, 0, {
            duration: this.animationDuration,
            easingFunction: this.easingFunction
        });

        scrollPlayer
            .onScrollEnd()
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe(() => {
                this.updateButtonVisibility();
            });

        this.animationSubscription = scrollPlayer.start().pipe(takeUntil(this.destroy$)).subscribe();
    }

    private resizeCarousel() {
        setTimeout(() => {
            this.initCarousel();
            // if there is a focused game tile in this carousel, check if both carousel
            // container and the game tile is in the viewport after resizing.
            if (this.hasFocusedItem) {
                this.scrollFocusedItemIntoViewport();
            } else {
                // reset the carousel
                this.scrollLeft = 0;
            }
        });
    }

    // scroll the container and the focused item into view if one of them
    // out of viewport
    private scrollFocusedItemIntoViewport() {
        return combineLatest([
            this.spatialNavigationService.isFullyInViewport(document.activeElement),
            this.spatialNavigationService.isFullyInViewport(this.carousel.nativeElement)
        ])
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe(([aInviewport, bInviewport]) => {
                // if one of the element not in viewport
                if (!(aInviewport && bInviewport)) {
                    this.carousel.nativeElement.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center'
                    });
                    this.jumpToTile(this.currentFocusedIndex);
                }
            });
    }

    childrenChanged() {
        this.initCarousel();
    }

    onMouseEnter() {
        if (this.enableButtons) {
            this.carouselButtonsVisibility = true;
            this.changeDetector.detectChanges();
        }
    }

    onMouseLeave() {
        if (this.enableButtons) {
            this.carouselButtonsVisibility = false;
            this.changeDetector.detectChanges();
        }
    }

    private onScroll(event) {
        if (this.enableButtons) {
            this.updateButtonVisibility();
        }
    }

    focusFirstTile() {
        this.focusBackDirective.focusInitialFocusable();
    }

    private updateButtonVisibility() {
        /*  Note:
            1. Carousel is in the initial state
               Hide left-button. Show right-button.
            2. Carousel has reached it's limits
               Show left-button. Hide right-button.
            3. Carousel is in the middle
               Show both the buttons.
            4. Width of all elements less than container width
               Hide both the buttons.
        */

        // Check start boundary condition
        const showLeft =
            this.carouselDirection === 'ltr'
                ? Math.floor(this.scrollLeft) > 0
                : Math.floor(this.scrollLimit - this.scrollLeft) > 0;
        if (showLeft !== this.showLeftCarouselButton) {
            this.showLeftCarouselButton = showLeft;
        }

        // Check end boundary condition
        const showRight =
            this.carouselDirection === 'ltr'
                ? Math.floor(this.scrollLimit - this.scrollLeft) > 0
                : Math.floor(this.scrollLeft) > 0;
        if (showRight !== this.showRightCarouselButton) {
            this.showRightCarouselButton = showRight;
        }

        // reenter ng zone so angualr only detect changes once if we have come child callbacks
        // enters ng zone
        this.ngZone.run(() => {
            this.changeDetector.detectChanges();
        });
    }

    ngOnDestroy() {
        this.cancelScrollAnimationIfNeeded();
        this.destroy$.next(true);
        this.destroy$.complete();
        this.carousel.nativeElement.removeEventListener('navnotarget', this.navNoTarget);
    }

    tileIdentity(index: number, item: HigCarouselItem<T>): string {
        return item.id;
    }
}
